import * as excel from "@script/excel";
import type * as type from "@vTypes";
import { images } from "@img/imagesMap";
import * as mutate from "@script/mutate";
import MgmtTableHead_excel from "./MgmtTableHead_excel";
import { use, useEffect, useState } from "react";
import MgmtTableRowNewNurse_excel from "./MgmtTableRowNewNurse_excel";
import { useRef } from "react";
import { ReactSortable } from "react-sortablejs";
import LoadingDots from "@components/LoadingDots";

function MgmtExcelUpload({
  fullNurseSchema,
  preceptorList,
  fetchNurseSchema,
  setIsOpendExcelUpload,
}: {
  fullNurseSchema: type.FullNurseSchema[];
  preceptorList: { nurse_id: string; name: string }[];
  fetchNurseSchema: () => void;
  setIsOpendExcelUpload: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const [excelOriginData, setExcelOriginData] =
    useState<type.ParsingExcelData>();

  const [excelValidation, setExcelValidation] =
    useState<type.ParsingExcelData_validation[]>();
  const [excelFileName, setExcelFileName] = useState<string>();
  const [excelData, setExcelData] = useState<type.FullNurseSchema[]>();
  const [excelNewGroup, setExcelNewGroup] = useState<string[]>();
  const [willSaveCheckList, setWillSaveCheckList] = useState<boolean[]>();

  const [isDragging, setIsDragging] = useState(false);

  const [isUpload, setIsUpload] = useState<boolean>(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  function clickUploadButton() {
    if (excelData) {
      if (!confirm("기존의 데이터가 사라집니다.\n다시 업로드하시겠습니까?")) {
        return;
      }
    }
    fileInputRef.current?.click();
  }

  async function handleFileUpload(file) {
    setIsUpload(true);
    // 파일 유효성 검사
    if (file.size > 10 * 1024 * 1024) {
      alert("❌ 파일 크기는 10MB를 초과할 수 없습니다.");
      setIsUpload(false);
      return;
    }

    if (
      !file.name.toLowerCase().endsWith(".xlsx") &&
      !file.name.toLowerCase().endsWith(".xls")
    ) {
      alert("❌ 지원되지 않는 파일 형식입니다. (.xlsx, .xls만 지원)");
      setIsUpload(false);
      return;
    }
    setExcelFileName(file.name);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const uploadResult = await excel.upload(formData);
      if (!uploadResult.success) {
        // setIsUpload(false);
        return;
      }
      setExcelOriginData(uploadResult);
    } catch (error) {
      alert("업로드 실패");
    } finally {
      setIsUpload(false);
    }
  }

  async function updateExcelData() {
    let newSeq = fullNurseSchema.length;

    const reorderSequence = excelData?.map((row, index) => {
      return willSaveCheckList[index]
        ? { ...row, sequence: ++newSeq }
        : { ...row, sequence: 999 };
    });
    try {
      const result = await excel.confirm_upload(
        reorderSequence,
        willSaveCheckList,
        excelNewGroup
      );
      if (result.success) {
        alert("저장했습니다");
        setIsOpendExcelUpload(false);

        fetchNurseSchema();
      } else {
        alert("저장에 실패했습니다.");
      }
    } catch (error) {
      alert("저장에 실패했습니다", error);
    }
  }

  const ref_modal_excelUpload = useRef<HTMLDivElement | null>(null);
  const draggingRef = useRef(false);
  const offsetRef = useRef({ x: 0, y: 0 });

  const handleMouseDown = (e: React.MouseEvent) => {
    const modal = ref_modal_excelUpload.current;
    const mainDom = document.querySelector(".c_main__container") as HTMLElement;
    if (!modal || !mainDom) return;

    const modalRect = modal.getBoundingClientRect();
    const mainRect = mainDom.getBoundingClientRect();

    offsetRef.current = {
      x: e.clientX - modalRect.left,
      y: e.clientY - modalRect.top,
    };

    draggingRef.current = true;
  };
  const handleMouseMove = (e: MouseEvent) => {
    if (!draggingRef.current) return;

    const mainDom = document.querySelector(".c_main__container") as HTMLElement;
    const modal = ref_modal_excelUpload.current;
    if (!mainDom || !modal) return;

    const mainRect = mainDom.getBoundingClientRect();
    const modalRect = modal.getBoundingClientRect();

    const rawX = e.clientX - mainRect.left - offsetRef.current.x;
    const rawY = e.clientY - mainRect.top - offsetRef.current.y;

    const clampedX = Math.max(
      0,
      Math.min(rawX, mainRect.width - modalRect.width)
    );
    const clampedY = Math.max(
      0,
      Math.min(rawY, mainRect.height - modalRect.height)
    );

    const xPercent = (clampedX / mainRect.width) * 100;
    const yPercent = (clampedY / mainRect.height) * 100;

    // setPosition({ x: xPercent, y: yPercent });
    modal.style.left = `${xPercent}%`;
    modal.style.top = `${yPercent}%`;
  };

  const handleMouseUp = () => {
    draggingRef.current = false;
  };

  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  useEffect(() => {
    if (excelOriginData) {
      setExcelData(excelOriginData.data);
      setExcelValidation(excelOriginData.validation_results);
      setExcelNewGroup(excelOriginData.new_groups_needed);
      const checklist = excelOriginData.validation_results.map(
        (valid) => valid.is_valid
      );
      setWillSaveCheckList(checklist);
    }
  }, [excelOriginData]);

  return (
    <section
      className="modal_excelUpload modal_window"
      ref={ref_modal_excelUpload}
      style={{ left: "2%", top: "30px" }}
    >
      <header
        className="modal_header gridBox modal_moveHandler"
        onMouseDown={handleMouseDown}
      >
        <div className="gridItem--left"></div>
        <div className="gridItem--center">
          <h3 className="modal_title">근무자 엑셀 업로드</h3>
        </div>
        <div className="gridItem--right">
          <button
            type="button"
            className="modal_button_close"
            onClick={() => setIsOpendExcelUpload(false)}
          ></button>
        </div>
      </header>

      <div className="excel_modal_body">
        <div className="excel_dargArea_container">
          <button
            type="button"
            className="excel_template_download"
            onClick={excel.template_download}
          >
            <img src={images.icon_excel} alt="" />
            템플릿 다운로드
          </button>

          <div
            className={`excel_upload_dragArea ${isDragging ? "excel_upload_dragArea--dragging" : ""}`}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setIsDragging(false);
              if (excelData) {
                if (
                  !confirm(
                    "기존의 데이터가 사라집니다.\n다시 업로드하시겠습니까?"
                  )
                ) {
                  return;
                }
              }
              if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                handleFileUpload(e.dataTransfer.files[0]);
                e.dataTransfer.clearData();
              }
            }}
          >
            <div
              className={`excel_upload_fileName ${!excelFileName && "excel_upload_fileName--noFile"}`}
            >
              {excelFileName
                ? excelFileName
                : "파일을 여기로 끌어오거나 파일선택 버튼을 눌러 등록해주세요"}
            </div>
            <button
              type="button"
              className="excel_upload_button"
              onClick={clickUploadButton}
            >
              파일 선택
            </button>
          </div>
          <input
            type="file"
            className="excel_upload_inputFile"
            ref={fileInputRef}
            accept=".xlsx, .xls"
            onInput={(e) => {
              handleFileUpload(e.currentTarget.files[0]);
              fileInputRef.current.value = "";
            }}
          ></input>
        </div>

        {excelData && (
          <>
            <div className="excel_newGroup_wrap">
              <div className="excel_newGroup_title">
                전체 {excelOriginData?.summary.total}건
              </div>
              <div className="excel_newGroup_title">
                성공 {excelOriginData?.summary.valid}건
              </div>
              <div className="excel_newGroup_title">
                실패 {excelOriginData?.summary.error}건
              </div>
              {excelNewGroup && excelNewGroup?.length > 0 && (
                <div className="excel_newGroup_title">
                  신규 병동 ({excelNewGroup?.length}건)
                </div>
              )}
            </div>
            <div className="excel_newGroup_list">
              {excelNewGroup?.map((group, index) => {
                return (
                  <div key={index} className="excel_newGroup_item">
                    {group}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {excelData && (
          <>
            <div className="comp_table comp_table--scroll">
              <MgmtTableHead_excel />
              <div className="comp_table_scrollBox">
                {excelData?.map((item, index) => {
                  return (
                    <MgmtTableRowNewNurse_excel
                      key={index}
                      fullNurseSchema={fullNurseSchema}
                      preceptorList={preceptorList}
                      order={index}
                      excelData={item}
                      setExcelData={setExcelData}
                      excelValidation={excelValidation[index]}
                      fetchNurseSchema={fetchNurseSchema}
                      willSaveCheck={willSaveCheckList[index]}
                      setWillSaveCheckList={setWillSaveCheckList}
                    />
                  );
                })}
              </div>
            </div>
            <div className="excel_buttonGroup">
              <button
                type="button"
                className="comp_btn"
                onClick={updateExcelData}
              >
                저장
              </button>
            </div>
          </>
        )}
        {isUpload && (
          <div className="excel_uploading_bg">
            <div>업로드 중입니다</div>
            <LoadingDots />
            {/* <div className="excel_uploading_progressBar">
                            <div className="excel_uploading_progress_percentage" style={{width: uploadingPercentage+"%"}}></div>
                        </div> */}
          </div>
        )}
      </div>
    </section>
  );
}

export default MgmtExcelUpload;
